<template>
  <div class="applications-list-modern">
    <div v-if="applications.length === 0" class="no-applications-state">
      <div class="no-apps-icon">
        <i class="fas fa-layer-group"></i>
      </div>
      <h5>Nenhuma Aplicação Detectada</h5>
      <p>O sistema não encontrou aplicações neste servidor</p>
    </div>
    
    <div v-else>
      <!-- Filtros Modernos -->
      <div class="filters-section">
        <div class="filters-row">
          <div class="filter-group">
            <label class="filter-label">
              <i class="fas fa-filter me-1"></i>
              Tipo
            </label>
            <select v-model="typeFilter" class="filter-select">
              <option value="">Todos os tipos</option>
              <option value="laravel">Laravel</option>
              <option value="vuejs">Vue.js</option>
              <option value="unknown">Desconhecido</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">
              <i class="fas fa-traffic-light me-1"></i>
              Status
            </label>
            <select v-model="statusFilter" class="filter-select">
              <option value="">Todos os status</option>
              <option value="ok">OK</option>
              <option value="warning">Aviso</option>
              <option value="error">Erro</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">
              <i class="fas fa-search me-1"></i>
              Buscar
            </label>
            <input 
              v-model="searchFilter" 
              type="text" 
              class="filter-input" 
              placeholder="Nome ou caminho da aplicação..."
            >
          </div>
        </div>
      </div>
      
      <!-- Lista de Aplicações -->
      <div class="applications-table-container">
        <table class="applications-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Caminho</th>
              <th>Tamanho</th>
              <th>Produção</th>
              <th>Debug</th>
              <th>Log</th>
              <th>Status</th>
              <th>Última Verificação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="app in filteredApplications" :key="app.id" class="app-row">
              <td class="app-name-cell">
                <div class="app-name">{{ app.name }}</div>
                <div v-if="app.version" class="app-version">v{{ app.version }}</div>
                <span v-if="app.has_alerts" class="alert-indicator">
                  <i class="fas fa-exclamation-triangle" title="Tem alertas"></i>
                </span>
              </td>
              
              <td>
                <span class="type-badge" :class="getTypeBadgeClass(app.type)">
                  <i :class="getTypeIcon(app.type)" class="me-1"></i>
                  {{ getTypeLabel(app.type) }}
                </span>
              </td>
              
              <td class="app-path-cell">
                <code class="app-path">{{ app.path }}</code>
              </td>
              
              <td class="size-cell">
                <span class="size-value">{{ formatSize(app.size_mb) }}</span>
              </td>
              
              <td v-if="app.type === 'laravel'" class="env-cell">
                <span 
                  class="env-badge" 
                  :class="app.env_production ? 'env-production' : 'env-development'"
                >
                  <i :class="app.env_production ? 'fas fa-check' : 'fas fa-tools'" class="me-1"></i>
                  {{ app.env_production ? 'Produção' : 'Desenvolvimento' }}
                </span>
              </td>
              <td v-else class="na-cell">N/A</td>
              
              <td v-if="app.type === 'laravel'" class="debug-cell">
                <span 
                  class="debug-badge" 
                  :class="app.debug_enabled ? 'debug-enabled' : 'debug-disabled'"
                >
                  <i :class="app.debug_enabled ? 'fas fa-bug' : 'fas fa-shield-alt'" class="me-1"></i>
                  {{ app.debug_enabled ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td v-else class="na-cell">N/A</td>
              
              <td v-if="app.type === 'laravel'" class="log-cell">
                <span 
                  class="log-size" 
                  :class="getLogSizeClass(app.log_size_mb)"
                >
                  <i class="fas fa-file-alt me-1"></i>
                  {{ formatSize(app.log_size_mb) }}
                </span>
              </td>
              <td v-else class="na-cell">N/A</td>
              
              <td class="status-cell">
                <span 
                  class="status-badge" 
                  :class="getStatusBadgeClass(app.status)"
                >
                  <i :class="getStatusIcon(app.status)" class="me-1"></i>
                  {{ getStatusLabel(app.status) }}
                </span>
              </td>
              
              <td class="date-cell">
                <span class="last-check">
                  <i class="fas fa-clock me-1"></i>
                  {{ formatDate(app.last_check) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Estatísticas Modernas -->
      <div class="statistics-section">
        <div class="statistics-container">
          <div class="statistics-info">
            <i class="fas fa-layer-group me-2"></i>
            Mostrando {{ filteredApplications.length }} de {{ applications.length }} aplicações
          </div>
          
          <div class="statistics-badges">
            <div class="stat-badge stat-success">
              <i class="fas fa-check-circle me-1"></i>
              <span class="stat-number">{{ okCount }}</span>
              <span class="stat-label">OK</span>
            </div>
            <div class="stat-badge stat-warning">
              <i class="fas fa-exclamation-triangle me-1"></i>
              <span class="stat-number">{{ warningCount }}</span>
              <span class="stat-label">Avisos</span>
            </div>
            <div class="stat-badge stat-error">
              <i class="fas fa-times-circle me-1"></i>
              <span class="stat-number">{{ errorCount }}</span>
              <span class="stat-label">Erros</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApplicationsList',
  props: {
    applications: {
      type: Array,
      default: () => []
    },
    serverId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      typeFilter: '',
      statusFilter: '',
      searchFilter: ''
    }
  },
  computed: {
    filteredApplications() {
      let filtered = this.applications
      
      if (this.typeFilter) {
        filtered = filtered.filter(app => app.type === this.typeFilter)
      }
      
      if (this.statusFilter) {
        filtered = filtered.filter(app => app.status === this.statusFilter)
      }
      
      if (this.searchFilter) {
        const search = this.searchFilter.toLowerCase()
        filtered = filtered.filter(app => 
          app.name.toLowerCase().includes(search) ||
          app.path.toLowerCase().includes(search)
        )
      }
      
      return filtered
    },
    
    okCount() {
      return this.filteredApplications.filter(app => app.status === 'ok').length
    },
    
    warningCount() {
      return this.filteredApplications.filter(app => app.status === 'warning').length
    },
    
    errorCount() {
      return this.filteredApplications.filter(app => app.status === 'error').length
    }
  },
  methods: {
    getTypeBadgeClass(type) {
      switch (type) {
        case 'laravel':
          return 'type-laravel'
        case 'vuejs':
          return 'type-vue'
        default:
          return 'type-unknown'
      }
    },
    
    getTypeIcon(type) {
      switch (type) {
        case 'laravel':
          return 'fab fa-laravel'
        case 'vuejs':
          return 'fab fa-vuejs'
        default:
          return 'fas fa-code'
      }
    },
    
    getTypeLabel(type) {
      switch (type) {
        case 'laravel':
          return 'Laravel'
        case 'vuejs':
          return 'Vue.js'
        default:
          return 'Desconhecido'
      }
    },
    
    getStatusBadgeClass(status) {
      switch (status) {
        case 'ok':
          return 'status-ok'
        case 'warning':
          return 'status-warning'
        case 'error':
          return 'status-error'
        default:
          return 'status-unknown'
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'ok':
          return 'fas fa-check-circle'
        case 'warning':
          return 'fas fa-exclamation-triangle'
        case 'error':
          return 'fas fa-times-circle'
        default:
          return 'fas fa-question-circle'
      }
    },
    
    getStatusLabel(status) {
      switch (status) {
        case 'ok':
          return 'OK'
        case 'warning':
          return 'Aviso'
        case 'error':
          return 'Erro'
        default:
          return 'Desconhecido'
      }
    },
    
    getLogSizeClass(sizeMb) {
      const size = parseFloat(sizeMb)
      
      if (!size || isNaN(size)) {
        return 'text-muted'
      }
      
      if (size > 100) {
        return 'text-danger font-weight-bold'
      } else if (size > 50) {
        return 'text-warning'
      } else {
        return 'text-success'
      }
    },
    
    formatSize(sizeMb) {
      // Converte para número se for string
      const size = parseFloat(sizeMb)
      
      // Verifica se é um número válido
      if (!size || isNaN(size) || size === 0) {
        return '0 MB'
      }
      
      if (size >= 1024) {
        return (size / 1024).toFixed(1) + ' GB'
      } else {
        return size.toFixed(1) + ' MB'
      }
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('pt-BR')
    }
  }
}
</script>

<style scoped>
.applications-list-modern {
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  min-height: 200px;
}

/* Estado Vazio */
.no-applications-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.no-apps-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
}

.no-applications-state h5 {
  margin-bottom: 8px;
  color: #374151;
  font-weight: 600;
}

/* Filtros Modernos */
.filters-section {
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.filter-select,
.filter-input {
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  background: white;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Tabela Moderna */
.applications-table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.applications-table {
  width: 100%;
  border-collapse: collapse;
}

.applications-table thead th {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 16px 12px;
  text-align: left;
  border: none;
}

.app-row {
  transition: all 0.3s ease;
  border-bottom: 1px solid #f3f4f6;
}

.app-row:hover {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  transform: translateY(-1px);
}

.applications-table td {
  padding: 14px 12px;
  font-size: 0.875rem;
  vertical-align: middle;
  border: none;
}

/* Células Específicas */
.app-name-cell {
  min-width: 180px;
}

.app-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.app-version {
  font-size: 0.75rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.alert-indicator {
  margin-left: 8px;
  color: #f59e0b;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.app-path-cell {
  max-width: 200px;
}

.app-path {
  background: #f8fafc;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
  border: 1px solid #e5e7eb;
  word-break: break-all;
}

.size-cell .size-value {
  font-weight: 500;
  color: #374151;
}

.na-cell {
  color: #9ca3af;
  font-style: italic;
}

/* Badges Modernos */
.type-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.type-laravel {
  background: linear-gradient(135deg, #ff2d20, #f56565);
  color: white;
}

.type-vue {
  background: linear-gradient(135deg, #41b883, #35495e);
  color: white;
}

.type-unknown {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.env-badge {
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.env-production {
  background: linear-gradient(135deg, #059669, #047857);
  color: white;
}

.env-development {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

.debug-badge {
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.debug-enabled {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.debug-disabled {
  background: linear-gradient(135deg, #16a34a, #15803d);
  color: white;
}

.log-size {
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

.status-ok {
  background: linear-gradient(135deg, #059669, #047857);
  color: white;
}

.status-warning {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

.status-error {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.status-unknown {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.last-check {
  font-size: 0.75rem;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
}

/* Classes de Log Size */
.text-danger {
  color: #dc2626 !important;
}

.text-warning {
  color: #d97706 !important;
}

.text-success {
  color: #059669 !important;
}

.text-muted {
  color: #6b7280 !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

/* Responsividade */
@media (max-width: 768px) {
  .filters-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .applications-table-container {
    overflow-x: auto;
  }
  
  .applications-table {
    min-width: 800px;
  }
}

/* Estatísticas Modernas */
.statistics-section {
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #fef3c7, #fbbf24);
  border-radius: 12px;
  border: 1px solid #f59e0b;
}

.statistics-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.statistics-info {
  font-size: 0.875rem;
  font-weight: 600;
  color: #92400e;
  display: flex;
  align-items: center;
}

.statistics-badges {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-badge {
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  color: white;
  min-width: 80px;
  justify-content: center;
}

.stat-success {
  background: linear-gradient(135deg, #059669, #047857);
}

.stat-warning {
  background: linear-gradient(135deg, #d97706, #b45309);
}

.stat-error {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.stat-number {
  font-weight: 700;
  font-size: 1rem;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.9;
}

/* Utilidade */
.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}
</style> 